import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Alert } from 'react-bootstrap';
import PromptLogsTable from '../components/common/PromptLogsTable';
import TokenUsageStatistics from '../components/common/TokenUsageStatistics';
import api from '../services/api';

const PromptHistoryPage = () => {
  // State management
  const [promptLogs, setPromptLogs] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tokenStats, setTokenStats] = useState(null);

  // Fetch prompt logs
  const fetchPromptLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getPromptLogs(
        sort,
        sortOrder,
        currentPage,
        pageSize
      );
      
      setPromptLogs(response.data?.prompt_logs || []);
      setTotalPages(response.data?.nr_of_pages || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prompt logs:', err);
      setError('Failed to load prompt logs. Please try again.');
      setLoading(false);
    }
  }, [sort, sortOrder, currentPage, pageSize]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle sort change
  const handleSortChange = (field, order) => {
    setSort(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Fetch token usage statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.getTokenUsageStatistics(3);
        setTokenStats(response.data);
      } catch (err) {
        console.error('Error fetching token statistics:', err);
        // Don't show error - statistics are optional
      }
    };
    fetchStats();
  }, []);

  // Fetch prompt logs on mount and when dependencies change
  useEffect(() => {
    fetchPromptLogs();
  }, [fetchPromptLogs]);

  return (
    <Container className="py-4">
      <h1 className="mb-4">Prompt History</h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tokenStats && <TokenUsageStatistics statistics={tokenStats} />}

      <Card>
        <Card.Header>
          <h5 className="mb-0">LLM Prompt Logs</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading prompt logs...</p>
            </div>
          ) : (
            <PromptLogsTable
              promptLogs={promptLogs}
              totalPages={totalPages}
              currentPage={currentPage}
              pageSize={pageSize}
              sort={sort}
              sortOrder={sortOrder}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PromptHistoryPage;
